import { logDebug } from '@ptek/client-logger';
import { useCallback, useMemo } from 'react';
import {
  ContextWithStandalone,
  StandaloneConfig,
} from '../types/standaloneMode';

/**
 * Helper function to determine if the context is in standalone mode
 * @param context - The context object containing standalone configuration
 * @returns true if in standalone mode, false otherwise
 */
export function isStandaloneMode(
  context: ContextWithStandalone | Record<string, unknown>
): boolean {
  return (
    context.standalone === true ||
    (typeof context.standalone === 'object' && context.standalone !== null)
  );
}

/**
 * Helper function to get auto-login configuration from context
 * @param context - The context object containing standalone configuration
 * @returns auto-login config if available, undefined otherwise
 */
export function getAutoLoginConfig(
  context: ContextWithStandalone
): StandaloneConfig['autoLogin'] | undefined {
  return typeof context.standalone === 'object' && context.standalone !== null
    ? context.standalone.autoLogin
    : undefined;
}

/**
 * Hook-like function to handle auto-login logic for standalone mode
 * @param options - Auto-login configuration options
 * @returns object with auto-login state and trigger function
 */
export function useStandaloneAutoLogin(options: {
  context: ContextWithStandalone;
  requireAuth: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { username: string; password: string }) => Promise<void>;
  autoLoginAttempted: { current: boolean };
  autoLoginFailed: { current: boolean };
}) {
  const {
    context,
    requireAuth,
    isAuthenticated,
    isLoading,
    login,
    autoLoginAttempted,
    autoLoginFailed,
  } = options;

  const autoLoginConfig = useMemo(() => getAutoLoginConfig(context), [context]);
  const isStandalone = useMemo(() => isStandaloneMode(context), [context]);

  const shouldAutoLogin = useMemo(
    () =>
      requireAuth &&
      !isAuthenticated &&
      !isLoading &&
      isStandalone &&
      autoLoginConfig &&
      !autoLoginAttempted.current &&
      !autoLoginFailed.current,
    [
      requireAuth,
      isAuthenticated,
      isLoading,
      isStandalone,
      autoLoginConfig,
      autoLoginAttempted,
      autoLoginFailed,
    ]
  );

  let loginTimeoutId: NodeJS.Timeout | null = null;

  const triggerAutoLogin = useCallback(async () => {
    if (!shouldAutoLogin || !autoLoginConfig) return;

    autoLoginAttempted.current = true;

    logDebug('StandaloneAutoLogin', 'Attempting auto-login', {
      username: autoLoginConfig.username,
    });

    // Bound auto-login to 10 seconds so a stalled login cannot hang the flow.
    loginTimeoutId = setTimeout(() => {
      logDebug('StandaloneAutoLogin', 'Auto-login timeout - marking as failed');
      autoLoginFailed.current = true;
      loginTimeoutId = null;
    }, 10000);

    try {
      await login({
        username: autoLoginConfig.username,
        password: autoLoginConfig.password,
      });
      if (loginTimeoutId) {
        clearTimeout(loginTimeoutId);
        loginTimeoutId = null;
      }
      logDebug('StandaloneAutoLogin', 'Auto-login succeeded');
    } catch (error) {
      if (loginTimeoutId) {
        clearTimeout(loginTimeoutId);
        loginTimeoutId = null;
      }
      logDebug('StandaloneAutoLogin', 'Auto-login failed', error);
      autoLoginFailed.current = true;
    }
  }, [
    shouldAutoLogin,
    autoLoginConfig,
    login,
    autoLoginAttempted,
    autoLoginFailed,
  ]);

  const resetAutoLoginState = useCallback(() => {
    const currentAutoLoginConfig = getAutoLoginConfig(context);
    const currentIsStandalone = isStandaloneMode(context);

    if (!currentIsStandalone || !currentAutoLoginConfig) {
      autoLoginAttempted.current = false;
      autoLoginFailed.current = false;
    }
  }, [context, autoLoginAttempted, autoLoginFailed]);

  const cleanup = useCallback(() => {
    if (loginTimeoutId) {
      clearTimeout(loginTimeoutId);
      loginTimeoutId = null;
    }
  }, []);

  return useMemo(
    () => ({
      shouldAutoLogin,
      triggerAutoLogin,
      resetAutoLoginState,
      cleanup,
      autoLoginConfig,
      isStandalone,
    }),
    [
      shouldAutoLogin,
      triggerAutoLogin,
      resetAutoLoginState,
      cleanup,
      autoLoginConfig,
      isStandalone,
    ]
  );
}
